import {Component, ElementRef, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Optional, Self} from '@angular/core';
import {ClientService} from '../../providers/client.service';
import {ControlValueAccessor, NgControl} from '@angular/forms';
import {Subject} from 'rxjs';
import {FocusMonitor} from '@angular/cdk/a11y';
import {LoggerFactory} from '@elderbyte/ts-logger';
import {MatFormFieldControl} from '@angular/material/form-field';

@Component({
  selector: 'tcc-client-selector',
  templateUrl: './client-selector.component.html',
  styleUrls: ['./client-selector.component.scss'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: ClientSelectorComponent
    }
  ]
})
export class ClientSelectorComponent implements OnInit, OnDestroy, MatFormFieldControl<string>, ControlValueAccessor {

  static nextId = 0;

  _logger = LoggerFactory.getLogger(this.constructor.name);

  _placeholder = '';
  _disabled = false;

  _clients: string[] = [];

  _onChange: any;
  _onTouched: any;

  @HostBinding('attr.aria-describedby') _describedBy: string;

  focused: boolean;
  @HostBinding() id = `tcc-client-selector-${ClientSelectorComponent.nextId++}`;
  @Input() required: boolean;
  readonly shouldPlaceholderFloat: boolean;
  _stateChanges: Subject<void> = new EventEmitter<void>();
  _value: string | null;


  constructor(public _clientService: ClientService,
              @Optional() @Self() public ngControl: NgControl,
              fm: FocusMonitor,
              elRef: ElementRef) {
    ngControl.valueAccessor = this;

    // Monitor element focus
    fm.monitor(elRef.nativeElement, true).subscribe(origin => {
      this.focused = !!origin;
      this.stateChanges.next();
    });
  }


  ngOnInit() {
    this.loadClients();
  }

  ngOnDestroy() {
    this.stateChanges.complete();
  }


  /**
   * Refresh the list of clients
   */
  loadClients() {
    this._clientService.getClients().subscribe(
      (clients: string[]) => {
        this._logger.debug(`Successfully received a list of clients: ${JSON.stringify(clients)}`);
        this._clients = clients;
      },
      (error: any) => {
        this._logger.error(`Failed to get clients: ${JSON.stringify(error)}`);
        this._clients = [];
      }
    );
  }


  get value() {
    return this._value;
  }
  set value(value: string) {
    this._value = value;

    this.stateChanges.next();
  }

  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(placeholder: string) {
    this._placeholder = placeholder;

    this.stateChanges.next();
  }

  @Input()
  get disabled() {
    return this._disabled;
  }
  set disabled(disabled: boolean) {
    this._disabled = disabled;
  }

  get empty() {
    return !this.value;
  }

  get shouldLabelFloat() {
    return this.empty;
  }

  get errorState() {
    return !!this.ngControl.errors;
  }

  get controlType() {
    return 'tcc-client-selector';
  }

  get describedBy() {
    return this._describedBy;
  }
  set describedBy(describedBy: string) {
    this._describedBy = describedBy;
  }

  get stateChanges() {
    return this._stateChanges;
  }
  set stateChanges(stateChanges: Subject<void>) {
    this._stateChanges = stateChanges;
  }


  // MatFormFieldControl implementation

  onContainerClick(event: MouseEvent): void {
  }

  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }


  // ControlValueAccessor implementation

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(obj: any): void {
    this.value = obj;
  }

}
